import { node } from '../node/Node';
import AttributeExpression from './expressions/AttributeExpression';
import CSSExpression from './expressions/CSSExpression';
import Expression, { hasExpression, snakeToCamel } from './expressions/Expression';
import TextExpression from './expressions/TextExpression';
import ViewExpression, { subscribeTo } from './expressions/ViewExpression';
import View from './View';
import { observable } from '../signal/Signal';


export interface ViewFactory {
	( type:string ):View;
}

export interface TemplateContext {
	data:() => {},
	childFactory:( type:string ) => View,
	view:() => View,
}


class Template {


	public root:Element;

	public context:TemplateContext;

	public expressions:Array<Expression> = [];
	public content:string;

	constructor( content:string ) {
		this.content = content || '';
	}

	render( target?:Element ) {

		// if there a target node given
		if( target instanceof Element ) {

			// only render if there is content to render
			if( this.content != '' ) {

				// render temp node to copy inner html
				let temp = node( this.content ).native;
				target.innerHTML = temp.innerHTML;

				// apply all attributes on target
				for( let i = 0; i < temp.attributes.length; i++ ) {
					let name = temp.attributes[i].name;
					target.setAttribute( name, temp.getAttribute( name ) );
				}
			}

		} else {
			target = node( this.content ).native;
		}

		this.root = target;

		this.root['_lnview'] = this.context.view();
	}

	destroy() {
		this.expressions.forEach( expr => expr.destroy() );
	}

	processNode( node:Element|Text ) {

		// do not parse script tags
		if( node.nodeName.toLowerCase() == 'script' ) return;

		if( isText( node ) ) {
			this.processTextNode( node );
			return
		}

		if( node.getAttribute( 'view' ) ) {
			this.processViewNode( node );
			return;
		}

		this.processAttributes( node );
		this.processChildren( node );

		function isText( node:ChildNode ): node is Text {
			return node.nodeType === 3 /* Node.TEXT_NODE */;
		}
	}

	processTextNode( node:Node ) {
		if( hasExpression( node.nodeValue ) ) {
			let expr = new TextExpression( node );
			this.expressions.push( expr );
		}
	}

	processViewNode( node:Element ) {
		let expr = new ViewExpression( node, this.context );
		this.expressions.push( expr );
	}

	processAttributes( node:Element ) {

		for( let i = 0; i < node.attributes.length; i++ ) {

			let name = node.attributes[i].name;

			// handle events
			if( name.indexOf( 'on-' ) == 0 ) {

				// get signal name an subscribe
				let signalName = snakeToCamel( name.replace( 'on-', '' ) );
				subscribeTo( observable( signalName, node ), node.getAttribute( name ), this.context.data(), this.context.view() );

				// remove attribute and adjust index so the next attribute won't be skipped
				node.removeAttribute( name );
				--i;

				continue;
			}

			// handle attribute expressions
			if( hasExpression( node.getAttribute( name ) ) ) {

				// handle css special
				let expr = ( name == 'class' ) ? new CSSExpression( node, node.getAttribute( name ) ) : new AttributeExpression( node, name, node.getAttribute( name ) );
				this.expressions.push( expr );
			}
		}
	}

	processChildren( node:Element ) {
		for (let i = 0; i < node.childNodes.length; i++) {
			this.processNode( <Element|Text> node.childNodes[i] );
		}
	}


	update( name?:string ) {
		this.expressions.forEach( exp => {
			if( exp.hasAttribute( name ) ) exp.update( this.context.data(), name );
		});
	}

	/**
	 * Returns the child view within this template.
	 * @param name The view with the given name
	 */
	first( name:string ) {
		var node = this.root.querySelector( '[view=' + name + ']' );
		return ( node && node[ '_lnview' ] ) ? node[ '_lnview' ] : null;
	}
}

export default Template;








